import { cn } from 'cn';
// import { useTranslation } from 'react-i18next';
import { type PropsWithChildren, type ReactNode } from 'react';
// import { i18n } from '~/utils';

import { Button, type ButtonProps } from '../Button';

type ActionProps = ButtonProps;

const primaryActionDefault: ActionProps = {
  buttonStyle: 'primary',
  color: 'primary',
  // text: i18n.t('button.primary.default', { ns: 'ui' }),
  text: 'Speichern',
  title: undefined,
  type: 'button',
};

const secondaryActionDefault: ActionProps = {
  buttonStyle: 'link',
  // text: i18n.t('button.secondary.default', { ns: 'ui' }),
  text: 'Abbrechen',
};

const tertiaryActionDefault: ActionProps = {
  buttonStyle: 'secondary',
  color: 'error',
  style: { marginLeft: 'auto' },
  // text: i18n.t('button.tertiary.default', { ns: 'ui' }),
  text: 'Löschen',
};

export type ActionsRowProps = PropsWithChildren<{
  readonly onCancel?: () => void;
  readonly primaryAction: ActionProps;
  readonly secondaryAction?: ActionProps;
  readonly secondaryActionComponent?: ReactNode;
  readonly shouldBeHidden?: boolean;
  readonly tertiaryAction?: ActionProps;
}> &
  ComponentStyling;

/**
 * Renders a row with primary, secondary, and tertiary actions.
 *
 * @param {PropsWithChildren<{
 *   onCancel?: () => void;
 *   primaryAction?: ActionProps;
 *   secondaryAction?: ActionProps;
 *   shouldBeHidden?: boolean;
 *   style?: React.CSSProperties;
 *   tertiaryAction?: ActionProps;
 * }> & ComponentStyling} props - The props for the ActionsRow component.
 * @return {JSX.Element} The rendered actions row component.
 */
export const ActionsRow = ({
  children,
  className,
  onCancel,
  primaryAction,
  secondaryAction,
  secondaryActionComponent,
  shouldBeHidden = false,
  style,
  tertiaryAction,
}: ActionsRowProps) => {
  // const { t } = useTranslation();

  const primaryActionProps: ActionProps = {
    ...primaryActionDefault,
    ...primaryAction,
  };
  if (primaryActionProps.disabled) {
    primaryActionProps.title = [
      primaryActionProps.title,
      // t('button.disabledTooltip', { ns: 'ui' }),
      '(momentan gesperrt)',
    ]
      .filter(Boolean)
      .join(' ');
    primaryActionProps.style = {
      ...primaryActionProps.style,
      cursor: 'not-allowed', // Does not work with Material UI b/c of pointer-events: none;
    };
  }

  const { ...secondaryActionProps }: ActionProps = {
    ...secondaryActionDefault,
    onClick: onCancel,
    ...secondaryAction,
  };

  const { ...tertiaryActionProps }: ActionProps = {
    ...tertiaryActionDefault,
    ...tertiaryAction,
  };

  return (
    <div
      className={cn('flex w-full items-center gap-4 p-4', className)}
      style={style}
    >
      <Button {...primaryActionProps} />
      {onCancel && !shouldBeHidden ? (
        <Button {...secondaryActionProps} />
      ) : null}
      {secondaryActionComponent && !shouldBeHidden
        ? secondaryActionComponent
        : null}
      {children}
      {tertiaryAction ? <Button {...tertiaryActionProps} /> : null}
    </div>
  );
};
